import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Form, Table } from 'react-bootstrap';

const STATUSES = ['Not Started', 'In Progress', 'Complete'];
const initialRows = () => [1, 2, 3].map((id) => ({ id, quantity: 0, status: 'Not Started', notes: '' }));
export const isTestTableUpdate = (value) => value?.version === 2
  && typeof value.room === 'string' && value.room.length > 0 && value.room.length <= 40
  && typeof value.clientId === 'string' && value.clientId.length > 0 && value.clientId.length <= 50
  && Number.isSafeInteger(value.revision) && value.revision > 0
  && [1, 2, 3].includes(value.rowId)
  && ((value.field === 'quantity' && Number.isInteger(value.value) && value.value >= 0 && value.value <= 9999)
    || (value.field === 'status' && STATUSES.includes(value.value))
    || (value.field === 'notes' && typeof value.value === 'string' && value.value.length <= 200));

export default function SignalRTestTable({ connection, canEdit }) {
  const [room, setRoom] = useState('demo');
  const [rows, setRows] = useState(initialRows);
  const [drafts, setDrafts] = useState({});
  const [error, setError] = useState('');
  const [receivedCount, setReceivedCount] = useState(0);
  const [clientId] = useState(() => crypto.randomUUID());
  const draftRef = useRef({});
  const revision = useRef(0);
  const requests = useRef(new Map());
  const setDraft = (key, draft) => {
    const next = { ...draftRef.current };
    if (draft) next[key] = draft; else delete next[key];
    draftRef.current = next;
    setDrafts(next);
  };

  useEffect(() => {
    setRows(initialRows()); draftRef.current = {}; setDrafts({}); setReceivedCount(0); setError('');
    const lastSeen = new Map();
    const receive = (action) => {
      const update = action?.payload?.testTable;
      if (action?.type !== 'signalr/diagnostic' || !isTestTableUpdate(update) || update.room !== room.trim()) return;
      const key = `${update.rowId}:${update.field}`;
      const requestId = action.payload.correlationId;
      const request = requests.current.get(requestId);
      if (request && update.clientId === clientId && update.rowId === request.update.rowId
        && update.field === request.update.field && update.value === request.update.value
        && update.revision === request.revision) {
        clearTimeout(request.timer);
        requests.current.delete(requestId);
        // An older echo must not erase text the user has typed since that send.
        if (draftRef.current[key]?.revision === request.revision) setDraft(key, null);
      }
      const sourceKey = `${update.clientId}:${key}`;
      if ((lastSeen.get(sourceKey) || 0) >= update.revision) return;
      lastSeen.set(sourceKey, update.revision);
      if (lastSeen.size > 500) lastSeen.delete(lastSeen.keys().next().value);
      setRows((current) => current.map((row) => row.id === update.rowId ? {
        ...row, [update.field]: update.value,
        updatedBy: update.clientId === clientId ? 'This browser' : `Browser ${update.clientId.slice(0, 8)}`,
        updatedAt: new Date().toLocaleTimeString(),
      } : row));
      setReceivedCount((count) => count + 1);
    };
    connection?.on('ReceiveAction', receive);
    const outstanding = requests.current;
    return () => {
      connection?.off('ReceiveAction', receive);
      outstanding.forEach((request) => clearTimeout(request.timer)); outstanding.clear();
    };
  }, [connection, room, clientId]);

  const sendCell = async (rowId, field) => {
    const key = `${rowId}:${field}`;
    const draft = draftRef.current[key];
    if (!draft || draft.sent || !canEdit || connection?.state !== 'Connected') return;
    const value = field === 'quantity' ? (String(draft.value).trim() === '' ? NaN : Number(draft.value)) : draft.value;
    const update = { version: 2, room: room.trim(), clientId, rowId, field, value, revision: draft.revision };
    if (!isTestTableUpdate(update)) { setError('Use a room name, a whole-number quantity from 0–9999, and notes up to 200 characters.'); return; }
    setError('');
    const id = crypto.randomUUID();
    const fail = (message) => {
      const request = requests.current.get(id);
      if (!request) return;
      clearTimeout(request.timer); requests.current.delete(id);
      if (draftRef.current[key]?.revision !== draft.revision) return;
      setDraft(key, { ...draft, sent: false, failed: true });
      setError(message);
    };
    requests.current.set(id, { update, revision: draft.revision, timer: setTimeout(() => fail('No cell echo after 15 seconds. Edit the cell or leave it again to retry.'), 15000) });
    setDraft(key, { ...draft, sent: true });
    try {
      await connection.send('SendAction', { type: 'signalr/diagnostic', payload: { correlationId: id, testTable: update } });
    } catch { fail('The cell update could not be sent. Check the connection and edit the cell to retry.'); }
  };

  const changeCell = (rowId, field, value) => {
    const key = `${rowId}:${field}`;
    setDraft(key, { value, revision: ++revision.current, sent: false });
    if (field === 'status') sendCell(rowId, field);
  };

  return <section aria-label="Live SignalR test table" className="border rounded p-3 mb-3">
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
      <h3 className="h5 mb-0">Live test table</h3>
      <Badge bg="secondary">{receivedCount} updates received</Badge>
      <span className="small text-muted">This browser: {clientId.slice(0, 8)}</span>
    </div>
    <Form.Group controlId="signalr-table-room" className="mb-2" style={{ maxWidth: 320 }}>
      <Form.Label>Test room</Form.Label>
      <Form.Control value={room} maxLength={40} onChange={(event) => setRoom(event.target.value)} />
    </Form.Group>
    <p className="small text-muted">Use the same room in both connected browsers. Quantity and notes send when you leave the cell or press Enter. Status changes send immediately.
      Sample data is temporary and resets on refresh, reconnect, or room change; earlier updates are not replayed. No job or database records are changed.</p>
    {error && <Alert variant="warning">{error}</Alert>}
    <Table bordered responsive size="sm" className="mb-0">
      <thead><tr><th>Sample item</th><th>Quantity</th><th>Status</th><th>Notes</th><th>Last received</th></tr></thead>
      <tbody>{rows.map((row) => <tr key={row.id}>
        <th scope="row">Test item {row.id}</th>
        {['quantity', 'status', 'notes'].map((field) => {
          const draft = drafts[`${row.id}:${field}`];
          const props = {
            'aria-label': `Item ${row.id} ${field}`, size: 'sm', value: draft?.value ?? row[field],
            disabled: !canEdit || connection?.state !== 'Connected' || !room.trim(),
            onChange: (event) => changeCell(row.id, field, event.target.value),
            onBlur: () => sendCell(row.id, field),
            onKeyDown: (event) => { if (event.key === 'Enter') { event.preventDefault(); sendCell(row.id, field); } },
          };
          return <td key={field}>
            {field === 'status' ? <Form.Select {...props}>{STATUSES.map((status) => <option key={status}>{status}</option>)}</Form.Select>
              : <Form.Control {...props} type={field === 'quantity' ? 'number' : 'text'} min={0} max={9999} step={1} maxLength={200} />}
            {draft && <small className={draft.failed ? 'text-danger' : 'text-muted'}>{draft.failed ? 'Not confirmed' : draft.sent ? 'Waiting for echo…' : 'Editing…'}</small>}
          </td>;
        })}
        <td className="small">{row.updatedAt ? `${row.updatedBy} · ${row.updatedAt}` : 'No updates yet'}</td>
      </tr>)}</tbody>
    </Table>
  </section>;
}
